/**
 * RGPD-compliant analytics loader.
 *
 * PostHog is loaded ONLY after the user has explicitly accepted analytics
 * cookies via the CookieConsentBanner. Loading analytics before consent
 * is a CNIL/GDPR violation in France/EU.
 *
 * Usage:
 *   - On app mount: call maybeInitAnalytics() to auto-init if user
 *     previously consented.
 *   - On consent given: call initAnalytics() to load immediately.
 *   - On consent revoked: call disableAnalytics() to opt out.
 */

const POSTHOG_KEY = 'phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE';
const POSTHOG_HOST = 'https://us.i.posthog.com';

let _posthogLoaded = false;

/**
 * Inject the PostHog snippet and initialize it.
 * Idempotent: safe to call multiple times.
 */
export function initAnalytics() {
  if (_posthogLoaded || typeof window === 'undefined') return;

  // PostHog snippet (official)
  !(function (t, e) {
    var o, n, p, r;
    e.__SV ||
      ((window.posthog = e),
      (e._i = []),
      (e.init = function (i, s, a) {
        function g(t, e) {
          var o = e.split('.');
          o.length === 2 && ((t = t[o[0]]), (e = o[1]));
          t[e] = function () {
            t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
          };
        }
        ((p = t.createElement('script')).type = 'text/javascript'),
          (p.crossOrigin = 'anonymous'),
          (p.async = !0),
          (p.src =
            s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') +
            '/static/array.js'),
          (r = t.getElementsByTagName('script')[0]).parentNode.insertBefore(p, r);
        var u = e;
        for (
          void 0 !== a ? (u = e[a] = []) : (a = 'posthog'),
            u.people = u.people || [],
            u.toString = function (t) {
              var e = 'posthog';
              return 'posthog' !== a && (e += '.' + a), t || (e += ' (stub)'), e;
            },
            u.people.toString = function () {
              return u.toString(1) + '.people (stub)';
            },
            o =
              'init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric'.split(
                ' '
              ),
            n = 0;
          n < o.length;
          n++
        )
          g(u, o[n]);
        e._i.push([i, s, a]);
      }),
      (e.__SV = 1));
  })(document, window.posthog || []);

  window.posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    // Respect Do Not Track browser setting
    respect_dnt: true,
  });

  _posthogLoaded = true;
}

/**
 * Disable analytics tracking. Call when user revokes consent.
 */
export function disableAnalytics() {
  if (typeof window !== 'undefined' && window.posthog?.opt_out_capturing) {
    window.posthog.opt_out_capturing();
  }
}

/**
 * Re-initialize analytics on app boot if the user previously consented.
 * Reads cookieConsent from localStorage.
 */
export function maybeInitAnalytics() {
  if (typeof window === 'undefined') return;

  try {
    const raw = localStorage.getItem('cookieConsent');
    if (!raw) return; // no consent yet → don't load

    const consent = JSON.parse(raw);
    if (consent && consent.analytics === true) {
      initAnalytics();
    }
  } catch (e) {
    // Corrupted consent → ignore, will re-prompt user
    console.warn('[analytics] could not parse cookieConsent', e);
  }
}
